/**
 * Shared SEO metadata logic. Pure ES module (no aliases, no React, no browser globals) so the SAME code runs in
 *  - the browser (components/common/SEO.jsx, and each page that builds its meta), and
 *  - the Node build step (scripts/prerender-seo.js), which writes real per-URL HTML so social crawlers — which do not run
 *    JavaScript — read the right title, description and Open Graph image.
 * Every description is derived from the page's own data (product / industry / article / category record, or the page's existing
 * copy); nothing is invented here beyond the generic fallbacks named below.
 */
import { SITE, siteUrl } from "../config/site.js";
import { clampText, joinNames } from "./seoText.js";
import { industryLineNames as lineNamesOf } from "./industryContent.js";

export { SITE, siteUrl };

const BRAND = SITE.name;
const isLocal = (host) => /^(localhost|127\.|0\.0\.0\.0|\[::1\]|10\.|192\.168\.)/.test(host || "");

/* ---------------------------------------------------------------- URLs */
/** "/x/y/?a=1#b" or "https://host/x/y/" → "/x/y" (root stays "/"). */
export function pathOf(input) {
  let p = String(input || "/");
  const m = p.match(/^https?:\/\/[^/]+(\/.*)?$/i);
  if (m) p = m[1] || "/";
  p = p.split("#")[0].split("?")[0];
  if (!p.startsWith("/")) p = `/${p}`;
  return p.length > 1 ? p.replace(/\/+$/, "") : "/";
}

/** Absolute canonical URL for a path (or for a URL on any host — the path is kept, the production origin replaces the host). */
export const canonicalUrl = (input, base) => `${base}${pathOf(input) === "/" ? "/" : pathOf(input)}`;

/** Absolute image URL. Own paths get the production origin; third-party https URLs are kept; localhost URLs are rewritten. */
export function absoluteImage(input, base) {
  const src = String(input || "").trim();
  if (!src) return `${base}${SITE.defaultImage}`;
  const m = src.match(/^https?:\/\/([^/]+)(\/.*)?$/i);
  if (m) return isLocal(m[1]) ? `${base}${m[2] || ""}` : src;
  return `${base}${src.startsWith("/") ? "" : "/"}${src}`;
}

export const isOwnOgImage = (image) => String(image || "").includes(SITE.ogImageDir) || String(image || "").endsWith(SITE.defaultImage);

/* ---------------------------------------------------------------- text */
export function clampDescription(text, max = 160) {
  return clampText(text, max);
}

export function splitName(name) {
  const m = String(name || "").match(/^(.*?)\s*\(([^)]*)\)\s*$/);
  return m && m[1] ? { title: m[1].trim(), subtitle: m[2].trim() } : { title: String(name || "").trim(), subtitle: "" };
}

/** One short line about a product: its own short description, else the opening line of its description. Never invented. */
export function productBlurb(product, max = 190) {
  const lines = String(product?.short_description || "").split("\n").map((l) => l.trim().replace(/[.;]$/, "")).filter(Boolean);
  const short = (lines.length > 1 ? lines.join(" · ") : lines[0] || "").replace(/\s*»\s*/g, " · ").replace(/^[\s·]+/, "");
  if (short.trim()) return clampText(short, max);
  const first = String(product?.description || "").split("\n").map((l) => l.trim()).find((l) => l && !/^[»•-]/.test(l));
  return first ? clampText(first, max) : "";
}

/* ---------------------------------------------------------------- OG images */
/** `/images/og/<kind>/<slug>.jpg` when that card exists (see data/ogManifest.json, written by scripts/build-og-images.mjs), else null. */
export function ogCard(kind, slug, og) {
  return og && Array.isArray(og[kind]) && og[kind].includes(slug) ? `${SITE.ogImageDir}${kind}/${slug}.jpg` : null;
}

/* ---------------------------------------------------------------- per-page meta builders
 * Each returns { title, description, path, image, imageAlt, type, keywords?, noindex? } — exactly the props of <SEO>. */

export function homeMeta({ cms, settings } = {}) {
  const cmsTitle = String(cms?.metaTitle || "").trim();
  const title = cmsTitle.length >= 25 ? cmsTitle : String(settings?.seo_default_title || "").trim() || SITE.defaultTitle;
  const description = clampDescription(cms?.metaDescription || cms?.description || settings?.seo_default_description || SITE.defaultDescription);
  return {
    title, description, path: "/", type: "website",
    keywords: cms?.keywords || settings?.seo_default_keywords || undefined,
    image: settings?.seo_og_image || cms?.ogImage || SITE.defaultImage,
    imageAlt: SITE.defaultImageAlt,
    // Admin's "Default OG Image" is specified as a 1200×630 card, so it keeps the og:image:width/height tags the static card has
    ogCardSize: !!settings?.seo_og_image,
  };
}

const nk = (t) => String(t || "").toLowerCase().replace(/[^a-z0-9]+/g, "");

export function productMeta(product, { og } = {}) {
  const cat = product.category_slug, sub = product.subcategory_slug;
  const seo = product.seo || {};
  const blurb = productBlurb(product, 140);
  const hasSpecs = Array.isArray(product.specifications) && product.specifications.length > 0;
  const tail = hasSpecs ? " View specifications and request a quote." : " Request a quote from Shingare Industries.";

  // A CMS title is kept unless it is merely a fragment of the product name (e.g. "Metric Bihexagon" for three different wrenches).
  const cmsTitle = String(seo.title || "").trim();
  const core = nk(cmsTitle.replace(/\|\s*Shingare Industries\s*$/i, ""));
  const fragment = core && nk(product.name).includes(core) && core.length < nk(product.name).length * 0.75;
  const title = cmsTitle && !fragment ? cmsTitle : `${product.name} | ${BRAND}`;

  // A CMS description under 60 characters is a data snippet ("Size Available : 3/8" O.D. …"), not a description: build one from the
  // product's own line instead (its name + that line, plus a pointer to what the page offers).
  let description = String(seo.description || "").trim();
  if (description.length < 60) {
    const lead = blurb && blurb.length >= 25 ? (blurb.length < 60 ? `${product.name}: ${blurb}` : blurb) : blurb ? `${product.name}: ${blurb}` : product.name;
    description = lead.length + tail.length <= 165 ? `${lead.replace(/[.…]$/, "")}.${tail}` : lead;
  }
  return {
    title,
    description: clampDescription(description),
    path: cat && sub ? `/products/${cat}/${sub}/${product.slug}` : `/products/${product.slug}`,
    image: ogCard("products", product.slug, og) || product.image || product.gallery?.[0],
    imageAlt: `${product.name} — ${BRAND}`,
    keywords: seo.keywords || undefined,
    type: "website",
  };
}

export function categoryMeta(category, { og } = {}) {
  const title = String(category.title || "").trim();
  return {
    title: `${title} | ${BRAND}`,
    description: clampDescription(category.description || `Explore Shingare Industries ${title} — models, specifications and quotations.`),
    path: `/products/${category.slug}`,
    image: ogCard("categories", category.slug, og) || category.image,
    imageAlt: `${title} — ${BRAND}`,
    type: "website",
  };
}

export function subcategoryMeta(category, subcategory, { og, products } = {}) {
  const t = String(subcategory.title || "").trim();
  // its own copy if the admin wrote any; else built from the subcategory's real product names; else the category's description
  const names = (products || []).map((p) => p.name).filter(Boolean);
  let generated = "";
  if (names.length) {
    // "<n> products including A and B" — shrink the list until it fits a snippet, so it never ends mid-phrase
    for (const k of [2, 1, 0]) {
      const listed = k ? ` including ${joinNames(names.slice(0, k))}` : "";
      const text = `${t} from Shingare Industries: ${names.length} product${names.length === 1 ? "" : "s"}${listed}.`;
      generated = text;
      if (text.length <= 158) break;
    }
  }
  return {
    title: `${t} — ${category.title} | ${BRAND}`,
    description: clampDescription(subcategory.hero_description || subcategory.intro_description || generated || category.description || `${t} from Shingare Industries — models, specifications and quotations.`),
    path: `/products/${category.slug}/${subcategory.slug}`,
    image: ogCard("categories", category.slug, og) || subcategory.hero_image || category.image,
    imageAlt: `${t} — ${BRAND}`,
    type: "website",
  };
}

/** Names of the product lines selected for an industry (Admin → Industries → Product Lines), for its description. */
export const industryLineNames = (industry, categories = []) => lineNamesOf(industry, categories);

export function industryMeta(industry, { og, categories } = {}) {
  const lines = industryLineNames(industry, categories);
  const description =
    industry.description ||
    `${industry.short_description || `${industry.name} solutions`}${lines.length ? `. Product lines: ${joinNames(lines.slice(0, 3))}.` : "."}`;
  return {
    title: `${industry.short_description || `${industry.name} Solutions`} | ${BRAND}`,
    description: clampDescription(description),
    path: `/industries/${industry.slug}`,
    image: ogCard("industries", industry.slug, og) || industry.hero_image,
    imageAlt: industry.image_alt || `${industry.name} — ${BRAND}`,
    type: "website",
  };
}

export function industriesListMeta(industries = []) {
  const n = industries.length;
  return {
    title: `Industries We Serve | ${BRAND}`,
    description: clampDescription(
      n ? `Tube cleaning, tube installation and maintenance equipment for ${n} industries including ${joinNames(industries.slice(0, 4).map((i) => i.name))}.` : "Tube cleaning, tube installation and maintenance equipment for the industries Shingare serves.",
    ),
    path: "/industries",
    type: "website",
  };
}

export function articleMeta(article, { og } = {}) {
  return {
    title: `${article.title} | ${BRAND}`,
    description: clampDescription(article.excerpt || `${article.title} — a technical guide from Shingare Industries.`),
    path: `/resources/engineering-articles/${article.slug}`,
    image: ogCard("articles", article.slug, og) || article.featured_image,
    imageAlt: article.featured_image_alt || article.title,
    type: "article",
  };
}

/** "Contact Shingare Industries (Maharashtra, India). Call +91 … or email … for product, technical, pricing and export enquiries." */
export function contactMeta(settings) {
  const oneLine = String(settings?.address || "").split(/\n+/).map((l) => l.trim().replace(/[,\s]+$/, "")).filter(Boolean).join(", ");
  const place = oneLine ? oneLine.replace(/\.$/, "").split(",").slice(-2).map((p) => p.trim()).join(", ") : "";
  const phone = String(settings?.phone || settings?.mobile || "").trim();
  const email = String(settings?.email || "").trim();
  const reach = [phone && `Call ${phone}`, email && `${phone ? "or email" : "Email"} ${email}`].filter(Boolean).join(" ");
  return {
    title: `Contact ${BRAND} | Industrial Engineering Solutions`,
    description: `Contact ${BRAND}${place ? ` (${place})` : ""}.${reach ? ` ${reach}` : ""} for product, technical, pricing and export enquiries.`,
    path: "/contact",
    type: "website",
  };
}

/** Fallback for any route that renders no <SEO> of its own: the site defaults (admin → Settings → SEO overrides them). */
export function siteDefaultMeta(settings, path = "/") {
  return {
    title: String(settings?.seo_default_title || "").trim() || SITE.defaultTitle,
    description: clampDescription(settings?.seo_default_description || SITE.defaultDescription),
    keywords: settings?.seo_default_keywords || undefined,
    image: settings?.seo_og_image || SITE.defaultImage,
    imageAlt: SITE.defaultImageAlt,
    ogCardSize: !!settings?.seo_og_image,
    path,
    type: "website",
  };
}

/**
 * Static pages: their existing titles and descriptions, moved here so the page component and the build-time HTML share one copy.
 * (Pages whose text depends on data — home, industries list, contact — have builders above.)
 */
export const STATIC_PAGES = {
  "/company/about": { title: "About Shingare | Industrial Engineering Solutions", description: "Since 1989, Shingare Industries has grown from a flexible shaft manufacturing workshop into an industrial engineering company supplying tube, bolting and pipe preparation tools to 45+ countries." },
  "/company/global-presence": { title: "Global Presence | Shingare Industries", description: "Shingare Industries is proud to serve clients in over 45+ countries across 6 continents. Our global footprint reflects our commitment to quality, reliability, and customer satisfaction." },
  "/company/manufacturing-facility": { title: "Manufacturing Facility | Shingare Industries", description: "Inside the Shingare Industries manufacturing facility — in-house engineering, production and quality control for our industrial tube, bolting and pipe preparation equipment." },
  "/company/quality-certifications": { title: "Quality & Certifications | Shingare Industries", description: "View and download Shingare Industries' quality certifications, including our ISO certification, and supporting quality documentation for our manufacturing and quality management practices." },
  "/resources": { title: "Resources | Shingare Industries", description: "Access product catalogues, datasheets, operation manuals, videos, case studies, FAQs, engineering articles and certificates from Shingare Industries." },
  "/resources/catalogues": { title: "Product Catalogues | Shingare Industries", description: "Explore Shingare Industries product and technical catalogues for tube tools, torque tools, pipe beveling, bolting and industrial maintenance solutions." },
  "/resources/datasheets": { title: "Technical Datasheets | Shingare Industries", description: "Explore Shingare Industries technical datasheets, product specifications, dimensions and performance information for industrial equipment and maintenance solutions." },
  "/resources/operation-manuals": { title: "Operation Manuals | Shingare Industries", description: "Installation, operation and maintenance manuals for Shingare Industries tube cleaning, expansion, beveling and bolting equipment." },
  "/resources/videos": { title: "Industrial Product Videos | Shingare Industries", description: "Watch Shingare Industries product demonstrations, installation walkthroughs and maintenance videos for industrial tube tools, torque tools and beveling equipment." },
  "/resources/case-studies": { title: "Industrial Case Studies | Shingare Industries", description: "Real Shingare Industries application case studies showing how our equipment solves maintenance and production challenges across industries." },
  "/resources/faqs": { title: "FAQs | Shingare Industries", description: "Answers to frequently asked questions about Shingare Industries products, applications, installation, maintenance, ordering and global support." },
  "/resources/engineering-articles": { title: "Engineering Articles | Shingare Industries", description: "In-depth technical guides and industry insights on tube cleaning, tube expansion, pipe beveling and industrial maintenance from Shingare Industries' engineering team." },
  "/resources/certificates": { title: "Quality Certifications | Shingare Industries", description: "Shingare Industries' quality management and product certifications, reflecting our commitment to international manufacturing and safety standards." },
  "/services": { title: "Industrial Services | Shingare Industries", description: "In-situ machining, controlled bolting and tube cleaning services from Shingare Industries — pipe cutting, beveling and flange facing on site, complete bolting workflow support, and chiller, boiler, condenser and heat exchanger tube cleaning." },
  "/services/in-situ-machining": { title: "In-Situ Machining Solutions | Shingare Industries", description: "On-site in-situ machining from Shingare Industries — pipe cutting and beveling, pipe end preparation and flange facing with portable electric, pneumatic and hydraulic machining equipment." },
  "/services/bolting-solutions": { title: "Bolting Solutions | Shingare Industries", description: "Controlled bolting from Shingare Industries — project planning, equipment selection, installation and tightening, site operations, post-job reporting and flange management with full joint traceability." },
  "/services/tube-cleaning": { title: "Tube Cleaning Services | Shingare Industries", description: "Tube cleaning services from Shingare Industries for chillers, boilers, condensers and heat exchangers, using mechanical brush / cutter cleaning and hydro jetting." },
  "/products": { title: "Industrial Engineering Products | Shingare Industries", description: "Browse Shingare Industries tube cleaners, tube expanders, pipe beveling machines, torque tools, flexible shaft grinders, tapping machines and more." },
};

export const staticMeta = (path) => {
  const s = STATIC_PAGES[path];
  return { title: s ? s.title : BRAND, description: clampDescription(s ? s.description : SITE.defaultDescription), path, type: "website" };
};

/* ---------------------------------------------------------------- head tags */
/**
 * Turns a page meta object into the complete list of <head> tags. Used by <SEO> (applies them to the DOM) and by the prerender
 * step (serialises them into HTML), so the two can never drift apart.
 *   base  production origin (no trailing slash)
 */
export function buildHead(meta, base) {
  const title = String(meta.title || "").trim() || SITE.defaultTitle;
  const description = String(meta.description || "").trim() || SITE.defaultDescription;
  const url = canonicalUrl(meta.path || "/", base);
  const image = absoluteImage(meta.image || SITE.defaultImage, base);
  const own = isOwnOgImage(image) || !!meta.ogCardSize;
  const alt = String(meta.imageAlt || (own && image.endsWith(SITE.defaultImage) ? SITE.defaultImageAlt : title));
  const type = meta.type === "article" ? "article" : "website";
  const m = (attr, name, content) => (content ? { tag: "meta", attr, name, content: String(content) } : null);
  const tags = [
    m("name", "description", description),
    meta.keywords ? m("name", "keywords", meta.keywords) : null,
    m("name", "robots", meta.noindex ? "noindex,follow" : null),
    { tag: "link", rel: "canonical", href: url },
    m("property", "og:type", type),
    m("property", "og:site_name", SITE.name),
    m("property", "og:title", title),
    m("property", "og:description", description),
    m("property", "og:url", url),
    m("property", "og:image", image),
    m("property", "og:image:width", own ? SITE.imageWidth : null),
    m("property", "og:image:height", own ? SITE.imageHeight : null),
    m("property", "og:image:alt", alt),
    m("name", "twitter:card", "summary_large_image"),
    m("name", "twitter:title", title),
    m("name", "twitter:description", description),
    m("name", "twitter:image", image),
    m("name", "twitter:image:alt", alt),
  ].filter(Boolean);
  return { title, tags, url, image };
}

/** Every tag `buildHead` can emit, as CSS selectors — lets <SEO> and the prerender step remove stale ones. */
export const MANAGED_SELECTORS = [
  'meta[name="description"]', 'meta[name="keywords"]', 'meta[name="robots"]', 'link[rel="canonical"]',
  'meta[property^="og:"]', 'meta[name^="twitter:"]',
];

const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
export const headToHtml = ({ title, tags }) =>
  [`<title>${esc(title)}</title>`, ...tags.map((t) => (t.tag === "link" ? `<link rel="${t.rel}" href="${esc(t.href)}" />` : `<meta ${t.attr}="${t.name}" content="${esc(t.content)}" />`))].join("\n    ");
